(() => {
  'use strict';

  if (window.__LLCartDrawerLazyMountV1) return;
  window.__LLCartDrawerLazyMountV1 = true;

  const PLACEHOLDER_SELECTOR = '[data-ll-cart-drawer-lazy-mount]';
  const CART_OPEN_SELECTOR = [
    'a[href$="/cart"]',
    'a[href*="/cart?"]',
    '.header__icon--cart',
    '.cart-icon-bubble',
    '.ll-cart-toggle',
    '[id^="ll-cart-button-"]'
  ].join(',');

  const OPEN_EVENTS = ['cart:open', 'cart-drawer:open'];
  const ADD_EVENTS = [
    'cart:add',
    'cart:added',
    'cart:item-added',
    'cart:product-added',
    'ajaxProduct:added',
    'product:added',
    'product:added-to-cart',
    'theme:cart:add',
    'theme:cart:added',
    'quick-add:added',
    'quickadd:added'
  ];

  let mountPromise = null;
  let mounted = !!document.querySelector('cart-drawer-component');
  let openRequested = false;
  let replayOpenScheduled = false;

  function placeholder() {
    return document.querySelector(PLACEHOLDER_SELECTOR);
  }

  function rootUrl() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root ? root : '/';
  }

  function sectionRequestUrl(sectionId) {
    const base = rootUrl();
    const joiner = base.includes('?') ? '&' : '?';
    return base + joiner + 'sections=' + encodeURIComponent(sectionId || 'cart-drawer');
  }

  function normalizedUrl(value) {
    try {
      return new URL(value, document.baseURI).href;
    } catch (error) {
      return String(value || '');
    }
  }

  function hasScript(src) {
    const target = normalizedUrl(src);
    if (!target) return false;
    return Array.from(document.scripts).some((script) => {
      return script.src && normalizedUrl(script.src) === target;
    });
  }

  function ensureStylesheet(href) {
    const target = normalizedUrl(href);
    if (!target) return Promise.resolve();

    const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((link) => {
      return link.href && normalizedUrl(link.href) === target;
    });

    if (existing) {
      if (existing.sheet) return Promise.resolve();
      return new Promise((resolve) => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', resolve, { once: true });
        window.setTimeout(resolve, 4000);
      });
    }

    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-ll-cart-drawer-lazy-css', 'true');
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });
      document.head.appendChild(link);
      window.setTimeout(resolve, 4000);
    });
  }

  function snapshotScripts(container) {
    return Array.from(container.querySelectorAll('script')).map((script) => ({
      src: script.getAttribute('src') || '',
      type: script.getAttribute('type') || '',
      text: script.textContent || '',
      attrs: Array.from(script.attributes).map((attr) => [attr.name, attr.value])
    }));
  }

  function copyScriptAttributes(target, attrs) {
    attrs.forEach(([name, value]) => {
      if (name === 'src' || name === 'defer' || name === 'async') return;
      try {
        target.setAttribute(name, value);
      } catch (error) {}
    });
  }

  function runScriptDescriptor(descriptor, mountRoot) {
    const type = String(descriptor.type || '').toLowerCase();

    if (!descriptor.src) {
      const inline = document.createElement('script');
      copyScriptAttributes(inline, descriptor.attrs);
      if (descriptor.type) inline.type = descriptor.type;
      inline.textContent = descriptor.text;
      (mountRoot || document.body).appendChild(inline);
      return Promise.resolve();
    }

    if (hasScript(descriptor.src)) return Promise.resolve();

    return new Promise((resolve) => {
      const external = document.createElement('script');
      copyScriptAttributes(external, descriptor.attrs);
      if (descriptor.type) external.type = descriptor.type;
      external.async = false;
      external.src = descriptor.src;
      external.setAttribute('data-ll-cart-drawer-lazy-script', 'true');
      external.addEventListener('load', resolve, { once: true });
      external.addEventListener('error', resolve, { once: true });
      document.body.appendChild(external);
      window.setTimeout(resolve, 2500);
    });
  }

  async function runSectionScripts(descriptors, mountRoot) {
    for (const descriptor of descriptors) {
      await runScriptDescriptor(descriptor, mountRoot);
    }
  }

  function requestOpenAfterMount() {
    if (!openRequested || replayOpenScheduled) return;
    replayOpenScheduled = true;

    requestAnimationFrame(() => {
      replayOpenScheduled = false;
      openRequested = false;

      const detail = { source: 'll-step79-cart-drawer-lazy-mount' };
      document.dispatchEvent(new CustomEvent('cart-drawer:open', { detail }));
    });
  }

  async function mountDrawer(options) {
    const config = options || {};
    if (config.open) openRequested = true;

    if (document.querySelector('cart-drawer-component')) {
      mounted = true;
      requestOpenAfterMount();
      return document.querySelector('cart-drawer-component');
    }

    if (mountPromise) {
      const result = await mountPromise;
      requestOpenAfterMount();
      return result;
    }

    const slot = placeholder();
    if (!slot) return null;

    const sectionId = slot.getAttribute('data-section-id') || 'cart-drawer';
    const cssUrl = slot.getAttribute('data-css-url') || '';

    mountPromise = (async () => {
      slot.setAttribute('data-ll-cart-drawer-loading', 'true');

      const cssPromise = ensureStylesheet(cssUrl);
      const response = await fetch(sectionRequestUrl(sectionId), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error('Cart drawer section request failed: ' + response.status);

      const payload = await response.json();
      const html = payload && payload[sectionId];
      if (!html) throw new Error('Cart drawer section HTML missing');

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sectionWrapper =
        parsed.querySelector('#shopify-section-' + sectionId) ||
        parsed.body.firstElementChild;

      if (!sectionWrapper) throw new Error('Cart drawer section markup missing');

      const descriptors = snapshotScripts(sectionWrapper);
      sectionWrapper.querySelectorAll('script').forEach((script) => script.remove());

      if (cssUrl) {
        const cssTarget = normalizedUrl(cssUrl);
        sectionWrapper.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
          if (normalizedUrl(link.getAttribute('href') || '') === cssTarget) link.remove();
        });
      }

      const imported = document.importNode(sectionWrapper, true);
      slot.replaceWith(imported);

      await cssPromise;
      await runSectionScripts(descriptors, imported);

      mounted = !!document.querySelector('cart-drawer-component');
      document.dispatchEvent(new CustomEvent('ll:cart-drawer:mounted', {
        detail: { mounted }
      }));

      return document.querySelector('cart-drawer-component');
    })();

    try {
      const result = await mountPromise;
      requestOpenAfterMount();
      return result;
    } catch (error) {
      console.error('[LucidLoom] Cart drawer lazy mount failed.', error);
      const fallbackRoot = rootUrl();
      if (openRequested) {
        window.location.href = fallbackRoot + (fallbackRoot.endsWith('/') ? 'cart' : '/cart');
      }
      return null;
    }
  }

  function isRealLucidCartHit(opener, eventTarget) {
    const lucidCartButton = opener.matches('.ll-cart-toggle, [id^="ll-cart-button-"]')
      ? opener
      : opener.closest('.ll-cart-toggle, [id^="ll-cart-button-"]');

    if (!lucidCartButton) return true;

    const trueHitbox = lucidCartButton.querySelector('.ll-cart-toggle__hitbox');
    return trueHitbox ? trueHitbox.contains(eventTarget) : !!eventTarget.closest('svg, .ll-cart-count');
  }

  function findCartOpener(target) {
    if (!target || !target.closest) return null;
    const opener = target.closest(CART_OPEN_SELECTOR);
    if (!opener || opener.closest('cart-drawer-component')) return null;
    return opener;
  }

  document.addEventListener('pointerdown', (event) => {
    if (mounted) return;
    const opener = findCartOpener(event.target);
    if (!opener || !isRealLucidCartHit(opener, event.target)) return;
    mountDrawer({ open: false });
  }, true);

  document.addEventListener('click', (event) => {
    if (mounted) return;

    const opener = findCartOpener(event.target);
    if (!opener || !isRealLucidCartHit(opener, event.target)) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    mountDrawer({ open: true });
  }, true);

  function handleOpenEvent() {
    if (mounted) return;
    mountDrawer({ open: true });
  }

  OPEN_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, handleOpenEvent, true);
    window.addEventListener(eventName, handleOpenEvent, true);
  });

  ADD_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, handleOpenEvent, true);
    window.addEventListener(eventName, handleOpenEvent, true);
  });

  window.LLLoadCartDrawer = function(options) {
    return mountDrawer(options || {});
  };
})();
